/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { toJS, action } from 'mobx';

import classname from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IOrderHistoryData } from './order-history.data';
import { IOrderHistoryProps } from './order-history.props.autogenerated';
import getSoldToIdsAction, { GetSoldToIdsInput } from '../../actions/get-sold-to-ids.action';
import getOrderHistoryAction, { GetOrderHistoryInput } from '../../actions/get-order-history.action';
import { IOrderHistoryTableData } from '../../actions/get-order-history.action';
import { Toaster, toast } from 'sonner';
import { ERROR_CODE_SERVER_ERROR_INDEX } from '../../configuration';

export interface IHeader {
    headerProps: INodeProps;
    heading: React.ReactNode;
    orderHistoryOrderCountLabel?: React.ReactNode;
    extraActions?: React.ReactNode;
}

export interface IOrderHistoryViewState {
    isDataReady: boolean;
    hasError: boolean;
    errorCode?: string;
    errorMessage?: string;
}

export interface IOrderHistoryViewProps extends IOrderHistoryProps<IOrderHistoryData> {
    orderHistoryProps: IModuleProps;
    pageSize: number;
    emptyMessage?: React.ReactNode;
    header: IHeader;
    viewState: IOrderHistoryViewState;
    orderHistoryTableData: IOrderHistoryTableData[] | undefined;
    soldTosFetched: boolean;
    soldToIdsData: string[];
    props: IOrderHistoryProps<IOrderHistoryData>;
}

/**
 *
 * OrderHistory component.
 * @extends {React.Component<IOrderHistoryProps<IOrderHistoryData>>}
 */
@observer
class OrderHistory extends React.Component<IOrderHistoryProps<IOrderHistoryData>> {
    @observable private viewState!: IOrderHistoryViewState;
    @observable private orderHistoryTableData!: IOrderHistoryTableData[] | undefined;
    @observable public soldToIdsData: string[] = [];
    @observable public fetchError: any = false;

    @observable private isSoldToIdsFetched: boolean = false;

    public async componentDidMount() {
        await this._getSoldToIds().then(async (response: string[] | null) => {
            const responseData = toJS(response);
            if (responseData && responseData?.length > 0) {
                await this.__getOrderHistoryTableData();
            }
            this.isSoldToIdsFetched = true;
        });
    }

    private _getSoldToIds = async () => {
        if (!this.isSoldToIdsFetched) {
            const actionInput = new GetSoldToIdsInput();
            const soldToData = await getSoldToIdsAction(actionInput, this.props.context.actionContext);
            if (soldToData.error && Number(String(soldToData.error.errorCode).charAt(0)) === ERROR_CODE_SERVER_ERROR_INDEX) {
                this._showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable);
                this.setFetchError(true);
            } else {
                this.setSoldToIdsData(soldToData);
            }
            return this.soldToIdsData;
        } else {
            return null;
        }
    };

    @action
    public setSoldToIdsData(soldToData: any) {
        this.soldToIdsData = toJS(soldToData.value);
    }

    @action
    public setFetchError(fetchError: boolean) {
        this.fetchError = fetchError;
    }

    @action
    public setOrderHistoryTableData(orderHistoryTableData: any) {
        this.orderHistoryTableData = toJS(orderHistoryTableData.value);
    }

    private __getOrderHistoryTableData = async () => {
        const ctx = this.props.context.actionContext;
        const actionInput = new GetOrderHistoryInput(ctx);
        const orderHistory = await getOrderHistoryAction(actionInput, this.props.context.actionContext);
        if (orderHistory.error && Number(String(orderHistory.error.errorCode).charAt(0)) === ERROR_CODE_SERVER_ERROR_INDEX) {
            this._showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable);
            this.setFetchError(true);
        } else {
            this.setOrderHistoryTableData(orderHistory);
        }
    };

    private _showToast = (heading: string, description: string) => {
        toast.error(
            <div className='toast' role='alert'>
                <div className='toast-header'>
                    <h4>{heading}</h4>
                    <button className='toast-close' onClick={() => toast.dismiss()}></button>
                </div>
                <div className='toast-body'>
                    <span>{description}</span>
                </div>
            </div>
        );
    };

    public render(): JSX.Element {
        const {
            config: { heading, pageSize, className },
            resources: { orderHistoryNoOrderHistoryLabel }
        } = this.props;

        let viewProps: IOrderHistoryViewProps = {
            ...this.props,

            viewState: this.viewState,
            orderHistoryProps: {
                moduleProps: this.props,
                className: classname('ms-order-history', className)
            },
            header: {
                headerProps: { className: 'ms-order-history__header' },
                heading: heading && heading.text && (
                    <Msdyn365.Text
                        {...heading}
                        className='ms-order-history__heading'
                        text={heading.text}
                        tag={heading.tag || 'h2'}
                        editProps={{ onEdit: this.handleOrderHistoryHeadingChange, requestContext: this.props.context.request }}
                    />
                )
            },
            pageSize: pageSize || 10,
            soldTosFetched: this.isSoldToIdsFetched,
            soldToIdsData: this.soldToIdsData,
            orderHistoryTableData: this.orderHistoryTableData,
            props: this.props
        };

        if (!this.orderHistoryTableData || this.orderHistoryTableData.length === 0) {
            if (this.viewState?.errorCode === 'Microsoft_Dynamics_Commerce_Runtime_AuthorizationFailed') {
                viewProps = {
                    ...viewProps,
                    emptyMessage: <p className='ms-order-history__empty-message'>{this.viewState.errorMessage}</p>
                };
            } else {
                viewProps = {
                    ...viewProps,
                    emptyMessage: <p className='ms-order-history__empty-message'>{orderHistoryNoOrderHistoryLabel}</p>
                };
            }
        }

        return (
            <>
                <Toaster
                    richColors
                    position='top-right'
                    toastOptions={{
                        classNames: {
                            default: 'toast-light',
                            error: 'toast-danger',
                            success: 'toast-success',
                            warning: 'toast-warning',
                            info: 'toast-info'
                        }
                    }}
                />
                {this.fetchError === true ? (
                    <>
                        <div className='alert alert-warning' role='alert'>
                            <span className='ms-order-history__alert-heading'>{this.props.resources.globalErrorServiceNotAvailable}</span>
                        </div>
                    </>
                ) : (
                    <>{this.props.renderView(viewProps) as React.ReactElement}</>
                )}
            </>
        );
    }

    public handleOrderHistoryHeadingChange = (event: Msdyn365.ContentEditableEvent) =>
        (this.props.config.heading!.text = event.target.value);
}

export default OrderHistory;
